import { useRef, useState } from 'react';
import { ApiError } from '../../../api';
import { uploadResumeFile, uploadUrl, type ResumeUploadRef } from '../client';

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';
const MATERIAL_ACCEPT = `${IMAGE_ACCEPT},application/pdf`;
const MAX_IMAGE_BYTES = 1.5 * 1024 * 1024;
const MAX_MATERIAL_BYTES = 4 * 1024 * 1024;

const ERROR_TEXT: Record<string, string> = {
  image_type: '只支持 JPEG / PNG / WebP 图片',
  image_invalid: '图片无法读取',
  asset_too_large: '图片超过 1.5 MB',
  material_too_large: '材料超过 4 MB',
  pdf_invalid: 'PDF 文件无效',
  pdf_not_allowed: '只有成果材料可以上传 PDF',
  http_413: '文件超过大小限制',
};

function errorText(error: unknown): string {
  if (error instanceof ApiError) return ERROR_TEXT[error.code] ?? error.code;
  if (error instanceof Error) return error.message;
  return '上传失败';
}

export default function UploadField({
  id,
  kind,
  value,
  allowPdf = false,
  hint,
  onChange,
}: {
  id: string;
  kind: 'portrait' | 'project' | 'conference' | 'internship' | 'achievement';
  value: ResumeUploadRef | null;
  allowPdf?: boolean;
  hint?: string;
  onChange: (value: ResumeUploadRef | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isPdf = value?.mime === 'application/pdf';

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    const max = file.type === 'application/pdf' ? MAX_MATERIAL_BYTES : MAX_IMAGE_BYTES;
    if (file.size > max) {
      setError(file.type === 'application/pdf' ? '材料超过 4 MB' : '图片超过 1.5 MB');
      return;
    }
    setBusy(true);
    try {
      const result = await uploadResumeFile(kind, file);
      onChange(result.upload);
    } catch (uploadError) {
      setError(errorText(uploadError));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="resume-upload">
      <input
        ref={inputRef}
        id={`${id}-input`}
        type="file"
        accept={allowPdf ? MATERIAL_ACCEPT : IMAGE_ACCEPT}
        hidden
        onChange={(event) => void pick(event.target.files?.[0])}
      />
      {value ? (
        <div className="resume-upload-preview">
          {isPdf ? (
            <span className="resume-upload-pdf">PDF</span>
          ) : (
            <img src={uploadUrl(value.id)} alt={value.name || '已上传照片'} />
          )}
          <div className="resume-upload-meta">
            <b>{value.name || (isPdf ? '成果材料.pdf' : '照片')}</b>
            <div className="resume-upload-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
              >
                {busy ? '上传中…' : '更换'}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy}
                onClick={() => onChange(null)}
              >
                移除
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          id={id}
          className="btn btn-ghost btn-sm"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? '上传中…' : allowPdf ? '上传照片或 PDF' : '上传照片'}
        </button>
      )}
      {error ? <span className="resume-upload-error">{error}</span> : null}
      {!error && hint ? <span className="resume-upload-hint">{hint}</span> : null}
    </div>
  );
}
